import React, { useState, useCallback } from "react";
import styled from "styled-components";
import { RendererProps } from "./index";
import { ColumnDef } from "../../../context/questionnaire/types";
import {
  ChoiceList,
  SelectWrapper,
  TextInput,
  FieldLabel,
  AddButton,
  RemoveButton,
} from "./renderer-styles";

interface NestedRow {
  values: Record<string, any>;
  is_valid_row: boolean;
}

interface FamilyMember {
  values: Record<string, any>;
  nested_rows: NestedRow[];
  is_valid_row: boolean;
}

const Card = styled.div<{ $active?: boolean }>`
  border: 2px solid ${({ $active }) => ($active ? "#0066cc" : "#e2e8f0")};
  border-radius: 12px;
  background: white;
  overflow: hidden;
  transition: border-color 0.15s ease;
`;

const CardHeader = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: ${({ $active }) => ($active ? "#f0f9ff" : "#f8fafc")};
  color: #4a4d5e;
  font-size: 16px;
  font-family: "Dosis", sans-serif;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  text-align: left;

  &:hover {
    background: #f0f9ff;
  }
`;

const CardBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid #e2e8f0;
`;

const StepSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CountInputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 12px;
`;

function emptyRow(cols: ColumnDef[]): Record<string, any> {
  const v: Record<string, any> = {};
  cols.forEach((c) => {
    v[c.name] = "";
  });
  return v;
}

function isRowFilled(values: Record<string, any>, cols: ColumnDef[]): boolean {
  return cols
    .filter((c) => c.required && c.type !== "remove_button")
    .every((c) => {
      const v = values[c.name];
      return v !== undefined && v !== "" && v !== null;
    });
}

function initMembers(
  answer: RendererProps["answer"],
  tierOne: ColumnDef[],
  tierTwo: ColumnDef[],
  prefilledRelationships?: string[]
): FamilyMember[] {
  if (answer?.value && Array.isArray(answer.value) && answer.value.length > 0) {
    return answer.value.map((r: any) => ({
      values: r.values || {},
      nested_rows: (r.nested_rows || []).map((nr: any) => ({
        values: nr.values || {},
        is_valid_row: nr.is_valid_row ?? false,
      })),
      is_valid_row: r.is_valid_row ?? false,
    }));
  }
  if (prefilledRelationships?.length) {
    return prefilledRelationships.map((rel) => ({
      values: { ...emptyRow(tierOne), relationship: rel },
      nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
      is_valid_row: false,
    }));
  }
  return [
    {
      values: emptyRow(tierOne),
      nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
      is_valid_row: false,
    },
  ];
}

function renderField(col: ColumnDef, value: any, onChange: (val: any) => void) {
  switch (col.type) {
    case "select":
    case "radio":
      return (
        <SelectWrapper
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">— Select —</option>
          {(col.options || []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.title || o.label}
            </option>
          ))}
        </SelectWrapper>
      );
    case "number":
      return (
        <TextInput
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
        />
      );
    case "date":
      return (
        <TextInput
          type="date"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <TextInput
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={col.title || ""}
        />
      );
  }
}

const FamilyHistoryRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const tierOne = question.columns_tier_one || [];
  const tierTwo = question.columns_tier_two || [];

  const isParents = question.name === "parents_cancer_history";
  const maxMembers = isParents ? 2 : Infinity;
  const useCountInput = !!question.count_description;
  const relativeType = question.strings?.relative_type || "relative";

  const relCol =
    tierOne.find((c) => c.name === "relationship") ||
    tierOne.find((c) => c.type === "select" || c.type === "radio");
  const parentRelationships =
    isParents && relCol?.options
      ? relCol.options.map((o) => o.value)
      : undefined;

  const [members, setMembers] = useState<FamilyMember[]>(() =>
    useCountInput &&
    (!answer?.value ||
      !Array.isArray(answer.value) ||
      answer.value.length === 0)
      ? []
      : initMembers(answer, tierOne, tierTwo, parentRelationships)
  );
  const [memberCount, setMemberCount] = useState<number>(() =>
    answer?.value && Array.isArray(answer.value) ? answer.value.length : 0
  );
  const [activeIdx, setActiveIdx] = useState<number | null>(
    useCountInput ? null : 0
  );

  const emit = useCallback(
    (next: FamilyMember[]) => {
      const tagged = next.map((m) => ({
        ...m,
        is_valid_row: isRowFilled(m.values, tierOne),
        nested_rows: m.nested_rows.map((nr) => ({
          ...nr,
          is_valid_row: isRowFilled(nr.values, tierTwo),
        })),
      }));
      const hasValid = tagged.some((m) => m.is_valid_row);
      onAnswer(tagged, hasValid);
      return tagged;
    },
    [onAnswer, tierOne, tierTwo]
  );

  const updateMemberField = useCallback(
    (idx: number, colName: string, val: any) => {
      setMembers((prev) => {
        const next = prev.map((m, i) =>
          i === idx ? { ...m, values: { ...m.values, [colName]: val } } : m
        );
        return emit(next);
      });
    },
    [emit]
  );

  const updateDetail = useCallback(
    (memberIdx: number, detailIdx: number, colName: string, val: any) => {
      setMembers((prev) => {
        const next = prev.map((m, i) => {
          if (i !== memberIdx) return m;
          const nrs = m.nested_rows.map((nr, ni) =>
            ni === detailIdx
              ? { ...nr, values: { ...nr.values, [colName]: val } }
              : nr
          );
          return { ...m, nested_rows: nrs };
        });
        return emit(next);
      });
    },
    [emit]
  );

  const addDetail = useCallback(
    (memberIdx: number) => {
      setMembers((prev) => {
        const next = prev.map((m, i) => {
          if (i !== memberIdx) return m;
          return {
            ...m,
            nested_rows: [
              ...m.nested_rows,
              { values: emptyRow(tierTwo), is_valid_row: false },
            ],
          };
        });
        return emit(next);
      });
    },
    [emit, tierTwo]
  );

  const removeDetail = useCallback(
    (memberIdx: number, detailIdx: number) => {
      setMembers((prev) => {
        const next = prev.map((m, i) => {
          if (i !== memberIdx) return m;
          const nrs = m.nested_rows.filter((_, ni) => ni !== detailIdx);
          return {
            ...m,
            nested_rows:
              nrs.length === 0
                ? [{ values: emptyRow(tierTwo), is_valid_row: false }]
                : nrs,
          };
        });
        return emit(next);
      });
    },
    [emit, tierTwo]
  );

  const addMember = useCallback(() => {
    setMembers((prev) => {
      const next = [
        ...prev,
        {
          values: emptyRow(tierOne),
          nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
          is_valid_row: false,
        },
      ];
      setActiveIdx(next.length - 1);
      return emit(next);
    });
  }, [emit, tierOne, tierTwo]);

  const removeMember = useCallback(
    (idx: number) => {
      setMembers((prev) => {
        const next = prev.filter((_, i) => i !== idx);
        if (useCountInput) setMemberCount(next.length || 0);
        if (next.length === 0) {
          if (useCountInput) {
            onAnswer([], true);
            return [];
          }
          const fresh = [
            {
              values: emptyRow(tierOne),
              nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
              is_valid_row: false,
            },
          ];
          setActiveIdx(0);
          return emit(fresh);
        }
        if (activeIdx !== null && activeIdx >= next.length) {
          setActiveIdx(next.length - 1);
        }
        return emit(next);
      });
    },
    [emit, tierOne, tierTwo, activeIdx, useCountInput, onAnswer]
  );

  const handleCountChange = useCallback(
    (newCount: number) => {
      const clamped = Math.max(
        0,
        Math.min(newCount, maxMembers === Infinity ? 20 : maxMembers)
      );
      setMemberCount(clamped);
      setMembers((prev) => {
        if (clamped === 0) {
          onAnswer([], true);
          return [];
        }
        if (clamped > prev.length) {
          const additions = Array.from(
            { length: clamped - prev.length },
            () => ({
              values: emptyRow(tierOne),
              nested_rows: [{ values: emptyRow(tierTwo), is_valid_row: false }],
              is_valid_row: false,
            })
          );
          const next = [...prev, ...additions];
          setActiveIdx(prev.length);
          return emit(next);
        }
        const next = prev.slice(0, clamped);
        if (activeIdx !== null && activeIdx >= clamped) {
          setActiveIdx(clamped - 1);
        }
        return emit(next);
      });
    },
    [emit, tierOne, tierTwo, maxMembers, activeIdx, onAnswer]
  );

  const summarize = (member: FamilyMember, idx: number): string => {
    const relCol =
      tierOne.find((c) => c.name === "relationship") ||
      tierOne.find((c) => c.type === "select" || c.type === "radio");
    const val = relCol ? member.values[relCol.name] : undefined;
    const opt = val ? relCol?.options?.find((o) => o.value === val) : undefined;
    const label = opt ? opt.title || opt.label || val : undefined;

    if (label) {
      const totalWithSameVal = members.filter(
        (m) => m.values[relCol!.name] === val
      ).length;
      if (totalWithSameVal <= 1) return label;
      let nth = 0;
      for (let i = 0; i <= idx; i++) {
        if (members[i].values[relCol!.name] === val) nth++;
      }
      return `${label} #${nth}`;
    }

    let nth = 0;
    for (let i = 0; i <= idx; i++) {
      const v = relCol ? members[i].values[relCol.name] : undefined;
      if (!v) nth++;
    }
    return `${relativeType} #${nth}`;
  };

  const detailCount = (member: FamilyMember): number =>
    member.nested_rows.filter((nr) => isRowFilled(nr.values, tierTwo)).length;

  return (
    <ChoiceList>
      {useCountInput && (
        <CountInputSection>
          <FieldLabel>{question.count_description || "How many?"}</FieldLabel>
          <TextInput
            type="number"
            min={0}
            max={maxMembers === Infinity ? undefined : maxMembers}
            value={memberCount || ""}
            onChange={(e) =>
              handleCountChange(parseInt(e.target.value, 10) || 0)
            }
            style={{ maxWidth: 120 }}
          />
        </CountInputSection>
      )}
      {members.map((member, idx) => (
        <Card key={idx} $active={activeIdx === idx}>
          <CardHeader
            role="button"
            tabIndex={0}
            $active={activeIdx === idx}
            onClick={() => setActiveIdx(activeIdx === idx ? null : idx)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActiveIdx(activeIdx === idx ? null : idx);
              }
            }}
          >
            <span
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                minWidth: 0,
                flex: 1,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {summarize(member, idx)}
              </span>
              {detailCount(member) > 0 && (
                <Badge style={{ flexShrink: 0 }}>
                  {detailCount(member)} condition(s)
                </Badge>
              )}
            </span>
            <span
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              {members.length > 1 && (
                <RemoveButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeMember(idx);
                  }}
                  aria-label={`Remove ${relativeType}`}
                  style={{ minWidth: 36, minHeight: 36, fontSize: 14 }}
                >
                  ✕
                </RemoveButton>
              )}
              <span>{activeIdx === idx ? "▲" : "▼"}</span>
            </span>
          </CardHeader>

          {activeIdx === idx && (
            <CardBody>
              <StepSection>
                <StepLabel>Step 1 — Relationship</StepLabel>
                {tierOne
                  .filter((c) => c.type !== "remove_button")
                  .map((col) => (
                    <div key={col.name}>
                      <FieldLabel>{col.title || col.name}</FieldLabel>
                      {renderField(col, member.values[col.name], (v) =>
                        updateMemberField(idx, col.name, v)
                      )}
                    </div>
                  ))}
              </StepSection>

              {tierTwo.length > 0 && (
                <StepSection>
                  <StepLabel>Step 2 — Conditions</StepLabel>
                  {member.nested_rows.map((nr, nIdx) => (
                    <DetailRow key={nIdx}>
                      {tierTwo
                        .filter((c) => c.type !== "remove_button")
                        .map((col) => (
                          <div key={col.name} style={{ flex: col.width || 1 }}>
                            <FieldLabel>{col.title || col.name}</FieldLabel>
                            {renderField(col, nr.values[col.name], (v) =>
                              updateDetail(idx, nIdx, col.name, v)
                            )}
                          </div>
                        ))}
                      {member.nested_rows.length > 1 && (
                        <RemoveButton
                          type="button"
                          onClick={() => removeDetail(idx, nIdx)}
                          aria-label="Remove condition"
                        >
                          ✕
                        </RemoveButton>
                      )}
                    </DetailRow>
                  ))}
                  <AddButton type="button" onClick={() => addDetail(idx)}>
                    + Add another condition
                  </AddButton>
                </StepSection>
              )}
            </CardBody>
          )}
        </Card>
      ))}
      {members.length < maxMembers && (
        <AddButton
          type="button"
          onClick={() => {
            if (useCountInput) setMemberCount((c) => c + 1);
            addMember();
          }}
        >
          + Add another {relativeType}
        </AddButton>
      )}
    </ChoiceList>
  );
};

export default FamilyHistoryRenderer;
